import { useEffect, useState } from 'react'
import { isOnServer } from '@shared/constants/util'

const mediaQueryMap = new Map<string, ReturnType<typeof createQueryCache>>()

const createQueryCache = (query: string) => {
  const mediaQueryList = window.matchMedia(query)
  const stateSetters = new Set<(matches: boolean) => void>()
  const eventListener = () => stateSetters.forEach((setState) => setState(mediaQueryList.matches))

  mediaQueryList.addEventListener('change', eventListener, { passive: true })

  const mediaQuery = { mediaQueryList, stateSetters, eventListener }
  mediaQueryMap.set(query, mediaQuery)

  return mediaQuery
}

export const useMedia = (query: string, defaultState: boolean | undefined): boolean => {
  const [state, setState] = useState(() => {
    if (defaultState !== undefined) return defaultState
    if (!isOnServer()) return window.matchMedia(query).matches
    return false
  })

  useEffect(() => {
    const mediaQuery = mediaQueryMap.get(query) ?? createQueryCache(query)
    const { stateSetters, mediaQueryList, eventListener } = mediaQuery

    stateSetters.add(setState)
    // this is fine since we are referencing the window object
    // oxlint-disable-next-line react-compiler/set-state-in-effect
    setState(mediaQueryList.matches)

    return () => {
      stateSetters.delete(setState)

      if (stateSetters.size === 0) {
        mediaQueryMap.delete(query)
        mediaQueryList.removeEventListener('change', eventListener)
      }
    }
  }, [query])

  return state
}
